<template>
  <div
    class="chart-container"
    :style="{
      height: props.report
        ? props.isMobile
          ? '300px'
          : '430px'
        : props.isMobile
        ? '300px'
        : '350px',
    }"
  >
    <v-chart
      class="chart"
      :option="chartOptions"
      :autoresize="true"
      :style="{ width: '100%', height: props.height }"
      ref="chartRef"
    />
    <el-button
      v-if="enableExport"
      @click="exportToExcel"
      class="export-btn"
      type="primary"
      size="small"
    >
      خروجی اکسل
    </el-button>
  </div>
</template>

<script setup>
import { computed, ref, defineProps } from "vue";
import VChart from "vue-echarts";
import * as echarts from "echarts/core";
import { LineChart, BarChart, PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
  ToolboxComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import jmoment from "moment-jalaali";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

jmoment.loadPersian({ usePersianDigits: false });
function toPersianDigits(input) {
  if (input === null || input === undefined) input = 0; // یا "" بسته به نیاز
  const persian = ["۰", "۱", "۲", "۳", "۴", "۵", "۶", "۷", "۸", "۹"];
  return String(input).replace(/\d/g, (d) => persian[d]);
}

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  DataZoomComponent,
  LegendComponent,
  ToolboxComponent,
  LineChart,
  BarChart,
  PieChart,
  CanvasRenderer,
]);

const props = defineProps({
  type: { type: String, default: "line" }, // line | bar | pie
  mode: { type: String, default: "daily" }, // daily | weekly | monthly | yearly | custom
  title: { type: String, default: "" },
  categories: { type: Array, default: () => [] }, // برای line/bar: ممکن است خالی باشد
  data: { type: Array, required: true }, // line/bar: [{name, data, unit, [categories]}], pie: [{name,value}]
  name: { type: String, default: "Series" }, // برای pie
  height: { type: String, default: "350px" },
  showZoom: { type: Boolean, default: true },
  isMobile: { type: Boolean, default: true },
  enableExport: { type: Boolean, default: true },
  report: { type: Boolean, default: false },
});

const chartRef = ref(null);

/* -------------------- کمکی‌های محور X -------------------- */
const formatClock = (dates) => {
  if (!Array.isArray(dates)) return [];
  return dates.map((d) => {
    if (typeof d !== "string") return "";
    const parts = d.split(" ");
    return parts.length > 1 ? toPersianDigits(parts[1]) : "";
  });
};

const formatWeek = (dates) => {
  if (!Array.isArray(dates)) return [];
  return dates.map((d) => {
    const jm = jmoment(d, ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"], true);
    if (!jm.isValid()) return d;
    let dayName = jm.format("dddd");
    if (dayName === "آدینه") dayName = "جمعه";
    return dayName;
  });
};

const formatMonth = (dates) => {
  if (!Array.isArray(dates)) return [];
  return dates.map((d) => {
    const jm = jmoment(d, ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"], true);
    return jm.isValid() ? jm.format("jMMMM") : d;
  });
};

const formatDate = (dates) => {
  if (!Array.isArray(dates)) return [];
  return dates.map((d) => toPersianDigits(d));
};

const isParsableJ = (d) =>
  jmoment(d, ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"], true).isValid();

const toMillisJ = (d) =>
  jmoment(d, ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"], true).valueOf();

/* اتحادی از دسته‌ها با حفظ ترتیب:
   1) اول props.categories (اگر بود)
   2) بعد categories هر سری (اگر داشت)
   اگر هیچ primary نداشتیم و همه قابل پارس بودند و mode زمانی بود (نه custom)، مرتب‌سازی زمانی می‌کنیم. */
/*const xDomainRaw = computed(() => {
  // Pie اصلاً محور ندارد
  if (props.type === "pie") return [];

  const out = [];
  const seen = new Set();

  const addList = (list) => {
    (list || []).forEach((v) => {
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    });
  };

  const hasSeriesCats = Array.isArray(props.data) && props.data.some(s => Array.isArray(s?.categories));
  const hasPrimaryCats = Array.isArray(props.categories) && props.categories.length > 0;

  if (hasPrimaryCats) addList(props.categories);
  if (hasSeriesCats) props.data.forEach((s) => addList(s.categories));

  // اگر هیچ‌کدام نبودند، و داده‌ی سری‌ها طول دارند، از ایندکس‌ها استفاده نکنیم
  // (در این حالت انتظار میره props.categories داده باشی؛ وگرنه محور بی‌معناست)
  if (!hasPrimaryCats && !hasSeriesCats) {
    addList(props.categories || []);
  }

  // اگر primary نداشتیم و حالت زمانی (غیر custom) هست و همه قابل پارس‌اند، مرتب‌سازی زمانی
  if (!hasPrimaryCats && props.mode !== "custom") {
    const allParsable = out.length > 0 && out.every(isParsableJ);
    if (allParsable) {
      out.sort((a, b) => toMillisJ(a) - toMillisJ(b));
    }
  }

  return out;
});*/
const xDomainRaw = computed(() => {
  // Pie اصلاً محور ندارد
  if (props.type === "pie") return [];

  const out = [];
  const seen = new Set();

  const addList = (list) => {
    (list || []).forEach((v) => {
      if (v !== undefined && v !== null && !seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    });
  };

  // آیا سری‌ها categories دارند؟
  const hasSeriesCats =
    Array.isArray(props.data) &&
    props.data.some(
      (s) => Array.isArray(s?.categories) && s.categories.length > 0
    );

  // آیا props.categories تعریف شده؟
  const hasPrimaryCats =
    Array.isArray(props.categories) && props.categories.length > 0;

  if (hasPrimaryCats) addList(props.categories);
  if (hasSeriesCats) props.data.forEach((s) => addList(s.categories));

  // اگر هیچ‌کدام نبودند، از props.categories استفاده کن
  if (!hasPrimaryCats && !hasSeriesCats) {
    addList(props.categories || []);
  }

  // اگر حالت زمانی و همه داده‌ها قابل پارس هستند، مرتب‌سازی
  if (!hasPrimaryCats && props.mode !== "custom") {
    const allParsable =
      out.length > 0 &&
      out.every(
        (d) =>
          d !== null &&
          d !== undefined &&
          jmoment(d, ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"], true).isValid()
      );
    if (allParsable) {
      out.sort((a, b) => {
        const aMillis = jmoment(
          a,
          ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"],
          true
        ).valueOf();
        const bMillis = jmoment(
          b,
          ["jYYYY/jMM/jDD HH:mm", "jYYYY/jMM/jDD"],
          true
        ).valueOf();
        return aMillis - bMillis;
      });
    }
  }

  return out;
});

/* لیبل‌های نهایی محور X (فقط نمایش)، اما ترتیب از xDomainRaw میاد */
const xAxisLabels = computed(() => {
  const raw = xDomainRaw.value;
  if (props.mode === "daily") return formatClock(raw);
  if (props.mode === "weekly") return formatWeek(raw);
  if (props.mode === "monthly") return formatMonth(raw);
  if (props.mode === "yearly") return formatDate(raw);
  if (props.mode === "custom") return raw; // مثل اسم چاه/ناحیه
  return raw;
});

/* -------------------- نرمالایزر سری‌ها -------------------- */
/* برای bar: نبود داده = 0  |  برای line: نبود داده = null */
/*function normalizeSeriesForAxis(series, xRaw, chartType) {
  const isBar = chartType === "bar";
  const fill = isBar ? 0 : null;

  // اگر سری خودش categories دارد، با xRaw هم‌تراز می‌کنیم
  if (Array.isArray(series.categories)) {
    const map = new Map();
    series.categories.forEach((cat, i) => {
      map.set(cat, series.data?.[i]);
    });
    const aligned = xRaw.map((x) => (map.has(x) ? map.get(x) : fill));
    return { ...series, data: aligned };
  }

  // اگر categories ندارد: از ترتیب xRaw و props.categories استفاده می‌شود
  // در این حالت فرض می‌گیریم طول data با xRaw هم‌اندازه باشد؛ اگر نبود، کمبودها fill
  const aligned =
    Array.isArray(series.data) && series.data.length
      ? xRaw.map((_, i) => (series.data[i] !== undefined ? series.data[i] : fill))
      : Array(xRaw.length).fill(fill);

  return { ...series, data: aligned };
}*/
function normalizeSeriesForAxis(series, xRaw, chartType) {
  const isBar = chartType === "bar";
  const fill = isBar ? 0 : null;

  // اگر series.categories دارد
  if (Array.isArray(series.categories) && series.categories.length) {
    const map = new Map();
    series.categories.forEach((cat, i) => {
      map.set(cat, series.data?.[i] ?? fill);
    });
    const aligned = xRaw.map((x) => (map.has(x) ? map.get(x) : fill));
    return { ...series, data: aligned, categories: xRaw };
  }

  // اگر categories ندارد: داده‌ها را با xRaw هم‌تراز کن
  const alignedData = xRaw.map((x, i) =>
    Array.isArray(series.data) && i < series.data.length ? series.data[i] : fill
  );

  return { ...series, data: alignedData, categories: xRaw };
}

/* -------------------- ساخت سری‌ها -------------------- */
function generateSeries(data, xRaw, props) {
  // Pie: یک سری واحد با داده‌ی props.data
  if (props.type === "pie") {
    return [
      {
        name: props.name,
        type: "pie",
        data: props.data, // [{name,value}]
        label: {
          show: true,
          position: "inside",
          formatter: function (params) {
            return `${params.name} : ${toPersianDigits(params.value)} ساعت`;
          },
        },
        radius: props.isMobile ? ["30%", "60%"] : ["50%", "80%"],
        center: ["50%", props.isMobile ? "50%" : "50%"],
      },
    ];
  }

  // Line/Bar
  if (!Array.isArray(data)) return [];

  return data.map((s) => {
    const normalized = normalizeSeriesForAxis(s, xRaw, props.type);

    return {
      name: normalized.name,
      type: props.type,
      // data: normalized.data,
      data: normalized.data.map((v) => ({ value: v, unit: normalized.unit })), // ← unit اضافه شد

      categories: normalized.categories, // ← اضافه شده
      seriesUnit: normalized.unit || "",
      sampling: props.type === "line" ? "average" : undefined,
      smooth: props.type === "line",
      lineStyle: props.type === "line" ? { width: 3 } : undefined,
      //barWidth: props.type === "bar" ? "40" : undefined,
       barMaxWidth: props.type === "bar" ? "40" : undefined,
        barMinWidth: props.type === "bar" ? "10" : undefined,
         barCategoryGap: props.type === "bar" ? "15px" : undefined,
      large: true,
      progressive: 500,
      itemStyle: { borderRadius: props.type === "bar" ? 6 : 0 },
      yAxisIndex: (() => {
        if (!normalized.unit) return 0;
        if (
          normalized.unit === "ساعت" &&
          props.data.some((s) => s.unit === "مترمکعب")
        )
          return 1;
        return 0;
      })(),
    };
  });
}

/* -------------------- Option نهایی -------------------- */
const chartOptions = computed(() => ({
  title: {
    text: props.title,
    left: "center",
    textStyle: {
      fontSize: props.isMobile ? 12 : 16,
      fontWeight: "bold",
    },
    top: 8,
  },

  tooltip: {
    trigger: props.type === "pie" ? "item" : "axis",
    backgroundColor: "#fff",
    borderColor: "#ccc",
    borderWidth: 1,
    confine: true,
    extraCssText: "max-height:230px; overflow-y:scroll;",
    textStyle: { color: "#333", align: "center", direction: "rtl" },
    // هدر فقط یک بار نمایش داده شود
    formatter: function (params) {
      // Pie
      if (!Array.isArray(params)) {
        const p = params;
        const color = p.color;
        return `
     <div style="display:block;align-items:center;gap:6px;direction:rtl;margin:2px 0;">
              <span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;"></span>
        <b>${p.name}</b>
        <hr>
        <div>${p.value + " ساعت "}</div>
        <div>${p.percent + " درصد "}</div>
      </div>
    `;
      }

      // Axis (line/bar)
      const header = params[0]?.axisValueLabel ?? params[0]?.name ?? "";
      const items = params
        .map((p) => {
          const color =
            typeof p.color === "string"
              ? p.color
              : p.color?.colorStops?.[0]?.color || "#999";
          const unit = p.data.unit || p.unit || "";

          return `
            <div style="display:flex;align-items:center;gap:6px;direction:rtl;margin:2px 0;">
              <span style="width:10px;height:10px;border-radius:50%;background:${color};display:inline-block;"></span>
              <span>${p.seriesName}</span>
              <span>${p.value} ${unit}</span>
            </div>
          `;
        })
        .join("");

      return `
        <div style="direction:rtl;line-height:1.6;padding:2px 0;">
          <div style="font-weight:bold;margin-bottom:6px;">${header}</div>
          ${items}
        </div>
      `;
    },
  },

  legend: {
    show: true,
    selectedMode: true, // کلیک روی legend غیرفعال
    orient:
      props.isMobile && window.innerWidth < 390 ? "vertical" : "horizontal",
    left:
      props.type === "pie" ? (props.isMobile ? "right" : "center") : "center",
    bottom: props.type === "pie" && props.isMobile ? 0 : 1,
    itemWidth: props.isMobile ? 12 : 20,
    itemHeight: props.isMobile ? 12 : 20,
    textStyle: { fontSize: props.isMobile ? 10 : 14 },
  },

  toolbox: {
    show: true,
    feature: {
      saveAsImage: { title: "ذخیره عکس" },
      restore: { title: "بازنشانی" },
      /* ...(props.isMobile
        ? {}
        : { dataView: { readOnly: true, title: "نمایش داده‌ها" } }),*/
    },
  },

  grid: { left: "3%", right: "4%", bottom: "12%", containLabel: true },

  dataZoom:
    props.showZoom && props.type !== "pie"
      ? [{ type: "inside" }, { type: "inside" }]
      : [],

  xAxis:
    
    props.type === "pie"
      ? undefined
      : {
          type: "category",
          data: xAxisLabels.value,
          axisLabel: { rotate: 45 },
        },

  yAxis:
    props.type === "pie"
      ? undefined
      : (() => {
          const units = [
            ...new Set(props.data.map((s) => s.unit).filter(Boolean)),
          ];
        const hasOnlyHour =( props.mode == "weekly" || props.mode == "yearly") && units.some(u => u == "ساعت");

          if (units.length === 1) {
            // فقط یک واحد
            return [
              {
                type: "value",
                name: units[0],
                axisLabel: { formatter: (v) => toPersianDigits(v) },
                position: "left",
                 max:hasOnlyHour ? 24 : undefined,
              },
            ];
          } else if (units.includes("ساعت") && units.includes("مترمکعب")) {
            // دو محور: مترمکعب چپ، ساعت راست
            return [
              {
                type: "value",
                name: "مترمکعب",
                axisLabel: { formatter: (v) => toPersianDigits(v) },
                position: "left",
                 
              },
              {
                type: "value",
                name: "ساعت",
                axisLabel: { formatter: (v) => toPersianDigits(v) },
                position: "right",
                  max: hasOnlyHour ? 24 : undefined,
              },
            ];
          } else {
            // حالت غیرمعمول، همه روی یک محور
            return [
              {
                type: "value",
                name: units.join(" / "),
                axisLabel: { formatter: (v) => toPersianDigits(v) },
                position: "left",
                 max:hasOnlyHour ? 24 : undefined,
              },
            ];
          }
        })(),
  series: generateSeries(props.data, xDomainRaw.value, props),
}));

/* -------------------- خروجی اکسل -------------------- */
function exportToExcel() {
  const worksheetData = [];

  // محور X واقعی برای اکسل (تاریخ کامل)
  const xDataForExcel = (() => {
    const allX = new Set();
    const hasSeriesCategories = props.data.some((s) =>
      Array.isArray(s.categories)
    );
    if (hasSeriesCategories) {
      props.data.forEach((s) => s.categories.forEach((d) => allX.add(d)));
    } else {
      props.categories.forEach((d) => allX.add(d));
    }
    return Array.from(allX).sort();
  })();

  // Pie chart
  if (props.type === "pie") {
    worksheetData.push(["بخش", "مقدار", "درصد"]);
    props.data.forEach((d) => {
      const value = d.value; //toPersianDigits
      const percent = d.percent ? d.percent : ""; //toPersianDigits
      worksheetData.push([d.name, value, percent]);
    });
  } else {
    // Line / Bar charts
    // Header
    const headerRow = ["تاریخ / دسته"];
    props.data.forEach((s) => headerRow.push(s.name));
    worksheetData.push(headerRow);

    // تعداد سطرها = طول محور X واقعی
    xDataForExcel.forEach((xLabel, i) => {
      const row = [xLabel]; // ستون تاریخ کامل
      props.data.forEach((series) => {
        let value;
        if (Array.isArray(series.categories)) {
          const idx = series.categories.indexOf(xLabel);
          value = idx >= 0 ? series.data[idx] : 0;
        } else {
          value = series.data[i] !== undefined ? series.data[i] : 0;
        }
        row.push(value); //toPersianDigits
      });
      worksheetData.push(row);
    });
  }

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "داده‌ها");
  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${props.title || "نمودار"}.xlsx`);
}
</script>

<style scoped>
.chart-container {
  position: relative;
}
.chart {
  width: 100%;
  height: v-bind(height);
}
.export-btn {
  position: absolute;
  left: 1rem;
  bottom: 0;
  z-index: 10;
  font-size: 10px;
}
.hide-export-excel-btn .export-btn {
  display: none !important;
}
.chart-container {
  width: 100%;
}
.chart-container {
  height: 100%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
